import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedApiService, ApiType } from '../services/api.service';
import { BodyAttachmentResponse } from '../models/attachment.model';

// Response interface for attachment operations
export interface AttachmentResponse {
  body: {
    id: string;
    type?: string;
    [key: string]: any;
  };
  headers: {
    get(name: string): string | null;
    [key: string]: any;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  // API endpoints
  uploadInternalCandidateFileURL = 'upload-internal-candidate-file';
  checkPermissionsURL = 'check-attachment-permissions';
  downloadCandidateAttachmentURL = 'get-candidate-attachments-id';
  cvUploadFileOpenAPI = 'api/upload-file';
  // Candidate avatar: upload sets users.photo_id + thumbnail; download serves full-res
  // via the HR attachment endpoint (same mechanism HR profile pictures use).
  uploadCandidateProfilePictureURL = 'upload-candidate-profile-picture';
  deleteCandidateProfilePictureURL = 'delete-candidate-profile-picture';
  downloadAttachmentURL = 'get-attachments-id';

  constructor(private sharedApiService: SharedApiService) { }

  /**
   * Send resume attachment to S3 bucket
   * @param data Blob data of the file
   * @param fileType MIME type of the file
   * @param fileName Name of the file
   * @param type Optional document type (cv, coverLetter, other)
   * @returns Observable with attachment response
   */
  sendCandidateAttachment(data: Blob, fileType: string, fileName: string, type?: string): Observable<BodyAttachmentResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('filetype', fileType);
    queryParams = queryParams.append('fileName', fileName);
    if (type) {
      queryParams = queryParams.append('type', type);
    }

    // Use the post method from ApiService
    return this.sharedApiService.post<BodyAttachmentResponse>(this.uploadInternalCandidateFileURL, data, queryParams, {
      // Override content type for file upload
      headers: this.sharedApiService.getHTTPHeaders().set('Content-Type', 'application/octet-stream'),
      apiType: ApiType.DEFAULT,
    });
  }

  /**
   * Upload the logged-in candidate's own profile picture (avatar).
   * The backend generates a thumbnail and sets users.photo_id, so the image is
   * returned as `photoThumbnail` on subsequent auth-user calls.
   * @param data Blob of the cropped image
   * @param fileType MIME type (image/*)
   * @param fileName Original file name
   * @returns Observable with the created attachment (id, url, thumbnail)
   */
  sendCandidateProfilePicture(data: Blob, fileType: string, fileName: string): Observable<BodyAttachmentResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('filetype', fileType);
    queryParams = queryParams.append('fileName', fileName);

    return this.sharedApiService.post<BodyAttachmentResponse>(this.uploadCandidateProfilePictureURL, data, queryParams, {
      headers: this.sharedApiService.getHTTPHeaders().set('Content-Type', 'application/octet-stream'),
      apiType: ApiType.DEFAULT,
    });
  }

  /**
   * Remove the logged-in candidate's profile picture (clears users.photo_id).
   */
  deleteCandidateProfilePicture(): Observable<BodyAttachmentResponse> {
    return this.sharedApiService.delete<BodyAttachmentResponse>(this.deleteCandidateProfilePictureURL, undefined, {
      apiType: ApiType.DEFAULT,
    });
  }

  /**
   * Download a full-resolution profile picture / HR attachment blob by id.
   * @param id Attachment ID (the user's photo_id)
   */
  downloadProfilePicture(id: string): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('id', id);

    return this.sharedApiService.get<Response>(this.downloadAttachmentURL, queryParams, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  sendNotLogInCandidateAttachment(data: Blob, fileType: string, type?: string): Observable<BodyAttachmentResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('filetype', fileType);
    if (type) {
      queryParams = queryParams.append('type', type);
    }

    return this.sharedApiService.post<BodyAttachmentResponse>(this.cvUploadFileOpenAPI, data, queryParams, {
      headers: this.sharedApiService.getHTTPHeaders().set('Content-Type', 'application/octet-stream'),
      apiType: ApiType.OPEN,
    });
  }

  /**
   * Upload high school education documents to S3 bucket
   * @param data Blob data of the file
   * @param fileType MIME type of the file
   * @param fileName Name of the file
   * @returns Observable with attachment response containing document details
   */
  uploadEducationDocument(data: Blob, fileType: string, fileName: string): Observable<BodyAttachmentResponse> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('filetype', fileType);
    queryParams = queryParams.append('fileName', fileName);

    // Use the post method from ApiService to upload to the same endpoint
    return this.sharedApiService
      .post<BodyAttachmentResponse>(this.uploadInternalCandidateFileURL, data, queryParams, {
        // Override content type for file upload
        headers: this.sharedApiService.getHTTPHeaders().set('Content-Type', 'application/octet-stream'),
        apiType: ApiType.DEFAULT,
      })
      .pipe(
        map((response) => {
          // Log successful upload
          if (response && response.body) {
            console.log('Education document uploaded successfully:', fileName);
          }
          return response;
        }),
        catchError((error) => {
          console.error('Error uploading education document:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Download a resume attachment by ID
   * @param id Attachment ID
   * @returns Observable with file response
   */
  downloadResumeAttachment(id: string): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('id', id);

    // Use the get method from ApiService with specific options for blob download
    return this.sharedApiService.get<Response>(this.downloadCandidateAttachmentURL, queryParams, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  /**
   * Process and download a file from response
   * @param response HTTP response containing the file
   */
  downloadResponseFile(response: AttachmentResponse): void {
    if (!response.body) {
      console.error('Response body is null');
      return;
    }

    const dataType = response.body['type'] || 'application/octet-stream';
    const binaryData: BlobPart[] = [];
    binaryData.push(new Blob([JSON.stringify(response.body)]));
    const objectUrl = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));

    const contentDisposition = response.headers.get('Content-Disposition');
    const fileName = contentDisposition ? contentDisposition.split(';filename=')[1] : 'download';
    const a: HTMLAnchorElement = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  /**
   * Helper method to determine file type from file name
   * @param fileName The name of the file
   * @returns The file type based on extension or generic document type
   */
  getFileTypeFromName(fileName: string | undefined): string {
    if (!fileName) return 'application/octet-stream';

    const extension = fileName.split('.').pop()?.toLowerCase();

    const mimeTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      txt: 'text/plain',
    };

    return extension && mimeTypes[extension] ? mimeTypes[extension] : 'application/octet-stream';
  }
}
